import isArray from 'lodash/isArray';
import React, { Fragment, useEffect, useState } from 'react';

import {
  apiAddFrontendFeatureFlag,
  apiGetFrontendFeatureFlagList,
  apiRemoveFrontendFeatureFlag,
  apiSaveFrontendFeatureFlagList,
} from 'src/api/featureFlags';
import { UiButton } from 'src/components/UI/Button';
import { UiCleanCheckbox } from 'src/components/UI/checkbox/CleanCheckbox';
import { UiCleanInput } from 'src/components/UI/inputs/CleanInput';
import { UiToast } from 'src/components/UI/toasts';
import { FlaggedRoutes, routes } from 'src/routes';
import { SafeAnyType } from 'src/utils/safeAny';
import { useStateForm } from 'src/utils/stateForm';
import { useStateFormFieldArray } from 'src/utils/stateForm/useStateFormFieldArray';

export type FrontendFeatureFlagsListType = Record<string, boolean>;
export type FrontendFeatureFlagSaveItemType = { Name: string; isEnabled: boolean };

type FormValues = {
  flags: { name: string; value: boolean; newOne: boolean }[];
};

/** just a page done as simply as it's possible */
export const FrontendFeatureFlagPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState<string[]>([]);

  const formProps = useStateForm<FormValues>({ mode: 'onBlur' });
  const { setValue, onSubmit } = formProps;

  const { fields, append, remove } = useStateFormFieldArray({
    formProps,
    name: 'flags',
  });

  useEffect(() => {
    apiGetFrontendFeatureFlagList()
      .then((response) => {
        setValue(
          'flags',
          Object.entries(response).map(([name, value]) => ({ name, value, newOne: false }))
        );
      })
      .catch(() => {
        UiToast.error('error getting list');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [setValue]);

  const list = routes
    .filter(({ path }) => {
      if (!isArray(path)) {
        return !path.startsWith('/test') && !['/frontendfeatureflag', '/whatpagesusereact'].includes(path);
      }
      return true;
    })
    .map(({ path }) => path)
    .flat();

  const flaggedRoutes: Record<string, string> = Object.fromEntries(
    Object.values(FlaggedRoutes).map((item) => [item.path, item.flag])
  );

  if (loading) {
    return <>Loading...</>;
  }
  return (
    <div style={{ width: '50%', margin: '0 auto', paddingBottom: 150 }}>
      <h3 className="text-center">Frontend feature flags manage page</h3>
      {fields.map((field, index) => (
        <Fragment key={field.id}>
          <div style={{ display: 'flex', border: '1px solid rgb(115, 115, 115)', padding: '10px', margin: '10px 0' }}>
            <UiCleanCheckbox
              formProps={formProps}
              label=""
              name={`flags[${index}].value`}
              defaultChecked={field.value}
              wrapperClassname="m-0"
            />
            <UiCleanInput
              formProps={formProps}
              name={`flags[${index}].name`}
              placeholder="Enter the flag's name"
              defaultValue={field.name}
              disabled={!field.newOne}
              className="m-0"
            />
            <UiCleanCheckbox
              wrapperClassname="display-none"
              formProps={formProps}
              label=""
              name={`flags[${index}].newOne`}
              defaultChecked={field.newOne}
            />
            <UiButton
              className="m-0"
              primary
              loading={removeLoading.includes(field.id)}
              onClick={() => {
                if (field.newOne) {
                  remove(index);
                  // eslint-disable-next-line no-alert
                } else if (window.confirm(`Are you sure you want to delete flag "${field.name}"?`)) {
                  setRemoveLoading((v) => [...v, field.id]);

                  apiRemoveFrontendFeatureFlag(field.name)
                    .then(() => {
                      UiToast.success(`Flag "${field.name}" successfully deleted`);
                    })
                    .catch(UiToast.error)
                    .finally(() => {
                      remove(index);

                      setRemoveLoading((currentRemoveLoading) => {
                        const foundIndex = currentRemoveLoading.findIndex((id) => id === field.id);

                        if (~foundIndex) {
                          const newArr = [...currentRemoveLoading];
                          newArr.splice(foundIndex, 1);
                          return newArr;
                        }
                        return currentRemoveLoading;
                      });
                    });
                }
              }}
            >
              X
            </UiButton>
          </div>
        </Fragment>
      ))}

      <UiButton
        primary
        onClick={onSubmit(({ flags }) => {
          const notEmptyFlags = flags.filter(({ name }) => name);

          setSaveLoading(true);

          /** save the new flags */
          notEmptyFlags
            .filter(({ newOne }, index) => {
              formProps.setValue(`flags[${index}].newOne` as SafeAnyType, false);

              return newOne;
            })
            .forEach(({ name, value }) => {
              apiAddFrontendFeatureFlag({ Name: name, isEnabled: value }).catch(UiToast.error);
            });

          /** update the existed flags */
          apiSaveFrontendFeatureFlagList(notEmptyFlags.map(({ name, value }) => ({ Name: name, isEnabled: value })))
            .then(() => {
              UiToast.success('successfully saved');
            })
            .catch(UiToast.error)
            .finally(() => {
              setSaveLoading(false);
            });
        })}
        loading={saveLoading}
      >
        Save
      </UiButton>
      <UiButton
        secondary
        onClick={() => {
          append({ name: '', value: false, newOne: true });
        }}
      >
        add
      </UiButton>

      <h3>List of URLs of pages using React</h3>

      <ul>
        {list.map((route) => (
          <div
            key={route.toString()}
            style={{
              display: 'flex',
              border: '1px solid rgb(115, 115, 115)',
              padding: '10px',
              margin: '10px 0',
              justifyContent: 'space-between',
              flexDirection: 'column',
            }}
          >
            <div>
              <strong>Url of page:</strong> {route}
            </div>
            {flaggedRoutes[route] ? (
              <div>
                <strong>FeatureFlag:</strong> {flaggedRoutes[route]}
              </div>
            ) : null}
          </div>
        ))}
      </ul>
    </div>
  );
};
